import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, AlertTriangle } from "lucide-react";
import { fmtQty } from "@/lib/constants";

export function EditWorkOrderDialog({ order, onClose, onSave }) {
  const produced = Number(order.produced_quantity || 0);
  const started = order.status === "In Progress" || produced > 0 || (order.stages || []).some((s) => (s.status || "pending") !== "pending");
  const [qty, setQty] = useState(String(order.target_quantity ?? ""));
  const [due, setDue] = useState(() => (order.due_date ? new Date(order.due_date).toISOString().slice(0, 16) : ""));
  const [saving, setSaving] = useState(false);
  const qtyNum = Number(qty);
  const qtyChanged = qtyNum !== Number(order.target_quantity);
  const warn = started && qtyChanged;
  const tooLow = qty !== "" && qtyNum < produced;
  const invalid = !(qtyNum > 0) || tooLow || !due;

  const submit = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await onSave(order.id, { target_quantity: qtyNum, due_date: new Date(due).toISOString() });
      onClose();
    } catch { /* onSave already toasts the error */ } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="wo-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil className="h-4 w-4 text-indigo-600" /> Edit Work Order {order.order_code}</DialogTitle>
          <DialogDescription className="text-xs">Adjust the target quantity or completion date. The schedule estimate and linked sales-order info update automatically on save.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Target Quantity ({order.unit || "pcs"})</Label>
            <Input type="number" min={produced > 0 ? produced : 1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="wo-edit-qty" />
            {produced > 0 && (
              <p className="text-[11px] text-slate-500" data-testid="wo-edit-produced-hint">Already produced: {fmtQty(produced, order.unit)} {order.unit || "pcs"} — the target can't go below this.</p>
            )}
            {tooLow && (
              <p className="text-[11px] text-red-600 font-medium" data-testid="wo-edit-qty-error">Target can't be lower than the {fmtQty(produced, order.unit)} already produced.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Target Completion Date</Label>
            <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="wo-edit-due" />
          </div>
          {warn && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="wo-edit-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-[12px] text-amber-800 font-medium">Work in progress. Are you sure you want to change the target?</p>
            </div>
          )}
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={invalid || saving} onClick={submit} data-testid="wo-edit-submit">
            {saving ? "Saving…" : warn ? "Yes, Save Changes" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
